import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import DefaultOrgLogo from '../../../../assets/images/DefaultOrgLogo.svg';
import InfoIcon from '../../../../assets/svgComponents/InfoIcon';
import Switch from '../../../../components/shared/Switch/Switch';

import styles from './Membership.module.scss';

const Membership = ({ memberships = [], handleProfileUpdate, isPublic }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const membershipsToDisplay = useMemo(() => {
    if (isPublic) {
      return memberships.filter(({ showOnProfile }) => showOnProfile);
    }
    return memberships;
  }, [memberships]);
  const membershipsCount = useMemo(() => membershipsToDisplay.length - 1, [membershipsToDisplay]);

  const handleSwitchChange = useCallback(
    (id) => {
      const updatedMemberships = memberships.map(({ org_id, showOnProfile }) => ({
        org_id,
        showOnProfile: org_id === id ? !showOnProfile : showOnProfile,
      }));
      handleProfileUpdate({ membership_json: updatedMemberships });
    },
    [memberships, handleProfileUpdate]
  );

  const handleOrgClick = useCallback((identifier) => {
    navigate(`/company/${identifier}`);
  }, [navigate]);

  return (
    membershipsToDisplay.length !== 0 && (
      <div className={styles.container}>
        <p className={styles.heading}>{t('profile.membership')}</p>
        {!isPublic && (
          <div className={styles.infoCont}>
            <InfoIcon />
            <p className={styles.infoText}>{t('profile.membershipInfo')}</p>
          </div>
        )}
        <div className={styles.membershipsCont}>
          {membershipsToDisplay.map((membership, index) => (
            <div key={`org-${membership.org_id}-${uuidv4()}`}>
              <div className={styles.membershipCont}>
                <div
                  className={styles.orgDetailsCont}
                  onClick={() => handleOrgClick(membership.org_identifier)}
                  role="presentation"
                >
                  <img src={membership.avatar ?? DefaultOrgLogo} alt="membership avatar" className={styles.avatar} />
                  <div className={styles.orgInfoCont}>
                    <p className={styles.name}>{membership.name}</p>
                    <p className={styles.info}>{membership.industry}</p>
                    <p className={styles.info}>
                      {membership.org_size}
                      {!membership.org_size.includes('employees') && ` ${t('employees')}`}
                    </p>
                  </div>
                </div>
                {!isPublic && (
                  <div className={styles.switchCont}>
                    <Switch checked={membership.showOnProfile} onChange={() => handleSwitchChange(membership.org_id)} />
                    <p className={styles.label}>{membership.showOnProfile ? t('public') : t('private')}</p>
                  </div>
                )}
              </div>
              {index !== membershipsCount && <div className={styles.divider} />}
            </div>
          ))}
        </div>
      </div>
    )
  );
};

export default Membership;
